// מסך 6 — אשף יצירת סט: הקשר → אתרים ומיקומים → כללי סגירה → מידות, חומרים וטקסטים → הנחיה → יצירה (AI) → וריאציות.
// כל מעבר שלב נשמר ב-wizardState כדי שאפשר יהיה לחדש עבודה. כשל יצירת AI אינו מוחק טיוטות.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { missingRequired, SPEC_FIELD_LABELS, type ResolvedSpec, type SpecFieldKey } from '@bb/schema';
import { api, waitForJob, type Job } from '../api';
import { Empty, ErrorBox, Field, fmtDate, Loading, useLoad, useToast } from '../components/ui';
import VariantThumb, { useVariantCtx } from '../components/editor/VariantThumb';
import JobProgress from '../components/editor/JobProgress';
import { AiStatusNote, useAiStatus } from '../components/editor/AiPanel';
import { SPEC_STATUS_LABEL } from '../components/editor/types';
import '../editor.css';

interface WS {
  step?: number;
  placementIds?: string[];
  /** placementId → ["300x250", ...] */
  sizes?: Record<string, string[]>;
  imageAssetId?: string | null;
  textKeys?: string[];
  lastGenerateJobId?: string | null;
}
interface SetResp { set: { id: string; name: string; campaign_id: string; prompt: string; wizard_state: WS; status: string; updated_at: string }; variants: VariantRow[] }
interface VariantRow { id: string; placement_id: string; width: number; height: number; display_name: string; current_revision: number; placement_name: string; media_name: string; spec_status: string; spec_is_demo: boolean; spec_version: number; last_export: any; needs_review?: boolean }
interface RuleRow { placement: any; site: { name: string; media_key: string }; spec: { id: string; version: number; status: string; is_demo: boolean; resolved: ResolvedSpec; conflicts?: any[] } | null; adapter: { id: string; name: string; status: string; is_demo: boolean } | null }

const STEPS = ['הקשר', 'אתרים ומיקומים', 'כללי סגירה', 'מידות, חומרים וטקסטים', 'הנחיה', 'יצירה', 'וריאציות'];

export default function SetWizard() {
  const { id } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  if (!id) return <NewSet campaignId={sp.get('campaignId')} />;
  return <WizardForSet key={id} setId={id} />;
}

// ── שלב 0 לסט חדש ──
function NewSet({ campaignId }: { campaignId: string | null }) {
  const nav = useNavigate();
  const [cid, setCid] = useState(campaignId ?? '');
  const camps = useLoad<any[]>(campaignId ? null : '/api/campaigns?limit=200');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const create = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post<SetResp>('/api/banner-sets', { campaignId: cid, name: name.trim(), wizardState: { step: 1 } });
      nav(`/sets/${r.set.id}`, { replace: true });
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="wz">
      <h1>סט באנרים חדש</h1>
      <Stepper step={0} maxStep={0} onGo={() => {}} />
      {!campaignId && (
        <div className="card">
          <Field label="קמפיין">
            <select value={cid} onChange={(e) => setCid(e.target.value)}>
              <option value="">בחרו קמפיין…</option>
              {camps.data?.map((c) => <option key={c.id} value={c.id}>{c.client_name} · {c.name}</option>)}
            </select>
          </Field>
          {camps.loading && <Loading />}
          <ErrorBox error={camps.error} />
        </div>
      )}
      {cid && <ContextCard campaignId={cid} />}
      <div className="card">
        <Field label="שם הסט" hint="משמש גם כמפתח ברירת מחדל לשם הבאנר בקישורים המתויגים">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} placeholder="למשל: השקה" />
        </Field>
        <ErrorBox error={err} />
        <div className="wz-nav"><span /><button className="btn primary" disabled={!cid || !name.trim() || busy} onClick={create}>צור סט והמשך ←</button></div>
      </div>
    </div>
  );
}

function ContextCard({ campaignId }: { campaignId: string }) {
  const c = useLoad<any>(`/api/campaigns/${campaignId}`);
  const cl = useLoad<any>(c.data ? `/api/clients/${c.data.campaign.client_id}` : null);
  if (c.error) return <ErrorBox error={c.error} />;
  if (!c.data) return <Loading />;
  const k = c.data.campaign;
  const brand = cl.data?.brands?.find((b: any) => b.id === k.brand_id);
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>הקשר</h2>
      <dl className="kv">
        <dt>לקוח</dt><dd>{cl.data ? <Link to={`/clients/${k.client_id}`}>{cl.data.client.name}</Link> : '…'}</dd>
        <dt>מותג</dt><dd>{brand?.name ?? '…'} <span className="muted small">(גרסת מותג בקמפיין: {c.data.brandVersion.version}{c.data.newerBrandVersion ? ' — קיימת גרסה חדשה יותר' : ''})</span></dd>
        <dt>קמפיין</dt><dd><Link to={`/campaigns/${k.id}`}>{k.name}</Link> <span className="mono small">{k.campaign_key}</span></dd>
        <dt>שפה</dt><dd>{k.language}</dd>
        <dt>URL ברירת מחדל</dt><dd className="mono">{k.default_url || '—'}</dd>
        {c.data.overridden?.length > 0 && <><dt>חריגות מהמותג</dt><dd>{c.data.overridden.join(', ')}</dd></>}
      </dl>
      <div className="row" style={{ marginTop: 8 }}>
        {c.data.kit.colors.map((x: any, i: number) => <span key={i} className="swatch" style={{ background: x.hex }} title={`${x.role} ${x.hex}`} />)}
        <span className="small muted">{[...new Set(c.data.kit.fonts.map((f: any) => f.family))].join(', ')}</span>
      </div>
    </div>
  );
}

function Stepper({ step, maxStep, onGo }: { step: number; maxStep: number; onGo: (s: number) => void }) {
  return (
    <nav aria-label="שלבי האשף">
      <ol className="wz-steps">
        {STEPS.map((s, i) => (
          <li key={s}><button aria-current={i === step ? 'step' : undefined} className={i < step ? 'done' : ''} disabled={i > maxStep || i === 0} onClick={() => onGo(i)}>{i + 1}. {s}</button></li>
        ))}
      </ol>
    </nav>
  );
}

// ── אשף לסט קיים ──
function WizardForSet({ setId }: { setId: string }) {
  const toast = useToast();
  const [data, setData] = useState<SetResp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ws, setWs] = useState<WS>({});
  const [prompt, setPrompt] = useState('');
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const load = useCallback(async () => {
    try {
      const r = await api.get<SetResp>(`/api/banner-sets/${setId}`);
      setData(r);
      return r;
    } catch (e: any) { setErr(e.message); return null; }
  }, [setId]);

  useEffect(() => {
    load().then((r) => {
      if (!r) return;
      const w = r.set.wizard_state ?? {};
      setWs(w);
      setPrompt(r.set.prompt ?? '');
      setStep(w.step ?? (r.variants.length ? 6 : 1));
    });
  }, [load]);

  const persist = useCallback(async (patch: Partial<WS>, extra: { prompt?: string } = {}) => {
    const next = { ...ws, ...patch };
    setWs(next);
    setSaving('saving');
    try {
      await api.patch(`/api/banner-sets/${setId}`, { wizardState: next, ...extra });
      setSaving('saved');
    } catch (e: any) { setSaving('error'); toast(`שמירת שלב נכשלה: ${e.message}`, 'err'); }
    return next;
  }, [ws, setId, toast]);

  const go = (s: number) => { setStep(s); void persist({ step: s }, { prompt }); window.scrollTo(0, 0); };

  if (err) return <ErrorBox error={err} />;
  if (!data) return <Loading />;
  const maxStep = data.variants.length ? 6 : Math.max(step, ws.step ?? 1, 4);

  return (
    <div className="wz">
      <div className="topbar">
        <div>
          <h1 style={{ marginBottom: 2 }}>סט: {data.set.name}</h1>
          <span className="small muted">נשמר אוטומטית בכל שלב — אפשר לצאת ולחזור. {saving === 'saving' ? 'שומר…' : saving === 'saved' ? 'נשמר.' : saving === 'error' ? 'שגיאת שמירה.' : `עודכן ${fmtDate(data.set.updated_at)}`}</span>
        </div>
        <div className="row">
          <Link className="btn" to={`/campaigns/${data.set.campaign_id}`}>לקמפיין</Link>
          {data.variants.length > 0 && <Link className="btn" to={`/exports?setId=${setId}`}>בדיקות ויצוא</Link>}
        </div>
      </div>
      <Stepper step={step} maxStep={maxStep} onGo={go} />
      <div aria-live="polite" className="sr-only">שלב {step + 1}: {STEPS[step]}</div>
      {step === 1 && <StepMedia ws={ws} onNext={(placementIds) => { void persist({ placementIds, step: 2 }); setStep(2); }} />}
      {step === 2 && <StepRules ws={ws} onBack={() => go(1)} onNext={() => go(3)} />}
      {step === 3 && <StepMaterials ws={ws} campaignId={data.set.campaign_id} variants={data.variants} onBack={() => go(2)} onNext={(p) => { void persist({ ...p, step: 4 }); setStep(4); }} />}
      {step === 4 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>הנחיה ליצירה</h2>
          <Field label="מה הבאנר צריך להעביר?" hint="ההנחיה נשלחת למודל יחד עם הבריף, הטקסטים המאושרים, ערכת המותג וכללי המפרט המאושרים. טקסט נעול לא ישונה.">
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={6} maxLength={5000} placeholder="למשל: הדגשת מחיר ההשקה, תחושה יוקרתית ושקטה, כניסה רכה של הכותרת ואז CTA בולט" />
          </Field>
          <div className="small muted">{prompt.length}/5000</div>
          <div className="wz-nav">
            <button className="btn" onClick={() => go(3)}>→ חזרה</button>
            <button className="btn primary" onClick={() => go(5)}>שמור והמשך ←</button>
          </div>
        </div>
      )}
      {step === 5 && <StepCreate setId={setId} ws={ws} prompt={prompt} data={data} reload={load} persist={persist} onBack={() => go(4)} onNext={() => go(6)} />}
      {step === 6 && <StepResults setId={setId} data={data} onBack={() => go(5)} />}
    </div>
  );
}

// ── שלב 1: אתרים ומיקומים ──
function StepMedia({ ws, onNext }: { ws: WS; onNext: (ids: string[]) => void }) {
  const sites = useLoad<any[]>('/api/media-sites');
  const [sel, setSel] = useState<string[]>(ws.placementIds ?? []);
  if (sites.error) return <ErrorBox error={sites.error} />;
  if (!sites.data) return <Loading />;
  const toggle = (id: string) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>בחירת אתרים ומיקומים</h2>
      {sites.data.length === 0 && <Empty>אין אתרי מדיה. <Link to="/media">הוספת אתר ומפרט</Link></Empty>}
      {sites.data.map((s) => (
        <fieldset key={s.id} className="pgroup">
          <legend>{s.name} <span className="mono small muted">{s.media_key}</span> {s.is_demo && <span className="badge">הדגמה</span>}</legend>
          {s.placements.length === 0 && <div className="small muted">אין מיקומים באתר זה.</div>}
          {s.placements.map((p: any) => {
            const noSpec = !p.current_spec_version_id && !(p.versions?.length);
            return (
              <label key={p.id} className="chk" style={{ display: 'flex', margin: '4px 0' }}>
                <input type="checkbox" checked={sel.includes(p.id)} disabled={noSpec} onChange={() => toggle(p.id)} />
                <span>{p.name}</span> <span className="mono small muted">{p.placement_key}</span> <span className="small muted">{p.device}</span>
                {noSpec ? <span className="badge err">אין מפרט — <Link to={`/media`}>להזנה</Link></span>
                  : <span className={`badge ${p.spec_status === 'approved' ? 'ok' : 'warn'}`}>מפרט {p.spec_version ? `v${p.spec_version} ` : ''}{SPEC_STATUS_LABEL[p.spec_status] ?? p.spec_status ?? 'ללא גרסה נוכחית'}</span>}
                {p.spec_is_demo && <span className="badge">מפרט הדגמה</span>}
              </label>
            );
          })}
        </fieldset>
      ))}
      <div className="wz-nav"><span className="small muted">{sel.length} מיקומים נבחרו</span><button className="btn primary" disabled={!sel.length} onClick={() => onNext(sel)}>המשך ←</button></div>
    </div>
  );
}

function useRules(ids: string[]) {
  const [rows, setRows] = useState<RuleRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const key = ids.join(',');
  useEffect(() => {
    if (!ids.length) { setRows([]); return; }
    api.post<RuleRow[]>('/api/placements/rules', { placementIds: ids }).then(setRows).catch((e) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { rows, err };
}

const fmtVal = (v: unknown) => v === 'unknown' ? <span className="badge warn">לא ידוע</span> : typeof v === 'boolean' ? (v ? 'כן' : 'לא') : Array.isArray(v) ? v.map((x) => typeof x === 'object' ? `${(x as any).width}×${(x as any).height}` : String(x)).join(', ') : String(v);

// ── שלב 2: כללי סגירה ──
function StepRules({ ws, onBack, onNext }: { ws: WS; onBack: () => void; onNext: () => void }) {
  const { rows, err } = useRules(ws.placementIds ?? []);
  if (err) return <ErrorBox error={err} />;
  if (!rows) return <Loading what="טוען כללי סגירה" />;
  const SHOW: SpecFieldKey[] = ['sizes', 'formats', 'zipMaxKb', 'unzippedMaxKb', 'weightBasis', 'maxDurationMs', 'maxLoops', 'clickMethod', 'clickTagName', 'entryFile', 'backupImageRequired', 'allowExternalRequests'];
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>מידות וכללי סגירה למיקומים שנבחרו</h2>
      <p className="small muted">הערכים מוצגים כפי שהם במפרט. שדה חסר מוצג כ"לא ידוע" — לא הונח ערך ברירת מחדל.</p>
      {rows.map((r) => {
        const spec = r.spec;
        const approved = spec?.status === 'approved';
        const unknown = spec ? (Object.keys(spec.resolved) as SpecFieldKey[]).filter((k) => (spec.resolved as any)[k] === 'unknown') : [];
        const missing = spec ? missingRequired(spec.resolved) : [];
        return (
          <section key={r.placement.id} className="pgroup" aria-label={`${r.site.name} ${r.placement.name}`}>
            <h3 style={{ margin: '4px 0' }}>{r.site.name} · {r.placement.name}</h3>
            <div className="row" style={{ marginBottom: 6 }}>
              {spec ? <span className={`badge ${approved ? 'ok' : 'warn'}`}>מפרט v{spec.version}: {SPEC_STATUS_LABEL[spec.status] ?? spec.status}</span> : <span className="badge err">אין מפרט</span>}
              {spec && !approved && <span className="badge warn">יצוא כטיוטה בלבד</span>}
              {spec?.is_demo && <span className="badge">מפרט הדגמה — לא של אתר אמיתי</span>}
              {r.adapter ? <span className={`badge ${r.adapter.status === 'approved' ? 'ok' : 'warn'}`}>adapter: {r.adapter.name} ({r.adapter.status === 'approved' ? 'מאושר' : 'ממתין לאישור'})</span> : <span className="badge err">אין click adapter — נדרשת קונפיגורציה ואישור</span>}
              {r.adapter?.is_demo && <span className="badge">adapter הדגמה</span>}
              {(spec?.conflicts?.length ?? 0) > 0 && <span className="badge err">{spec!.conflicts!.length} סתירות לא מוכרעות</span>}
              {spec && <Link className="small" to={`/spec-versions/${spec.id}`}>לפרטי המפרט</Link>}
            </div>
            {spec && (
              <dl className="kv">
                {SHOW.map((k) => <FragmentKV key={k} k={SPEC_FIELD_LABELS[k]} v={fmtVal((spec.resolved as any)[k])} />)}
              </dl>
            )}
            {missing.length > 0 && <div className="alert warn small" style={{ marginTop: 6 }}>שדות חובה למסירה שאינם ידועים: {missing.map((k) => SPEC_FIELD_LABELS[k]).join(', ')} — יצוא כטיוטה בלבד עד השלמה.</div>}
            {unknown.length > 0 && <div className="small muted" style={{ marginTop: 4 }}>שדות לא ידועים: {unknown.map((k) => SPEC_FIELD_LABELS[k]).join(', ')}</div>}
          </section>
        );
      })}
      <div className="wz-nav"><button className="btn" onClick={onBack}>→ חזרה</button><button className="btn primary" onClick={onNext}>המשך ←</button></div>
    </div>
  );
}
function FragmentKV({ k, v }: { k: string; v: React.ReactNode }) { return <><dt>{k}</dt><dd>{v}</dd></>; }

// ── שלב 3: מידות, חומרים וטקסטים ──
function StepMaterials({ ws, campaignId, variants, onBack, onNext }: { ws: WS; campaignId: string; variants: VariantRow[]; onBack: () => void; onNext: (p: Partial<WS>) => void }) {
  const { rows, err } = useRules(ws.placementIds ?? []);
  const camp = useLoad<any>(`/api/campaigns/${campaignId}`);
  const assets = useLoad<any[]>(camp.data ? `/api/assets?clientId=${camp.data.campaign.client_id}&campaignId=${campaignId}` : null);
  const [sizes, setSizes] = useState<Record<string, string[]>>(ws.sizes ?? {});
  const [manual, setManual] = useState<Record<string, string>>({});
  const [img, setImg] = useState<string | null>(ws.imageAssetId ?? null);
  const [texts, setTexts] = useState<string[] | null>(ws.textKeys ?? null);
  const [mErr, setMErr] = useState<string | null>(null);

  // ברירת מחדל: כל המידות המותרות; כל הטקסטים
  useEffect(() => {
    if (!rows || ws.sizes) return;
    const d: Record<string, string[]> = {};
    for (const r of rows) { const s = r.spec?.resolved.sizes; if (Array.isArray(s)) d[r.placement.id] = s.map((x) => `${x.width}x${x.height}`); }
    setSizes(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);
  const copy = camp.data?.campaign.copy;
  const allTextKeys = useMemo(() => copy ? [...(copy.headlines ?? []), ...(copy.texts ?? []), ...(copy.ctas ?? [])].map((x: any) => x.key).concat(copy.disclaimer ? ['disclaimer'] : []) : [], [copy]);
  useEffect(() => { if (texts === null && allTextKeys.length) setTexts(allTextKeys); }, [allTextKeys, texts]);
  useEffect(() => {
    if (img === null && ws.imageAssetId === undefined && assets.data) { const h = assets.data.find((a) => a.kind === 'image'); if (h) setImg(h.id); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets.data]);

  if (err || camp.error) return <ErrorBox error={err ?? camp.error} />;
  if (!rows || !camp.data) return <Loading />;
  const exists = (pid: string, sz: string) => variants.some((v) => v.placement_id === pid && `${v.width}x${v.height}` === sz);
  const toggle = (pid: string, sz: string) => setSizes((m) => { const cur = m[pid] ?? []; return { ...m, [pid]: cur.includes(sz) ? cur.filter((x) => x !== sz) : [...cur, sz] }; });
  const addManual = (pid: string) => {
    const m = /^\s*(\d{2,4})\s*[x×*]\s*(\d{2,4})\s*$/i.exec(manual[pid] ?? '');
    if (!m) { setMErr('פורמט מידה: רוחב x גובה, למשל 300x250'); return; }
    setMErr(null);
    const sz = `${Number(m[1])}x${Number(m[2])}`;
    setSizes((s) => ({ ...s, [pid]: [...new Set([...(s[pid] ?? []), sz])] }));
    setManual((x) => ({ ...x, [pid]: '' }));
  };
  const total = Object.values(sizes).reduce((n, a) => n + a.length, 0);
  const images = (assets.data ?? []).filter((a) => a.kind === 'image' || a.kind === 'background');
  const logos = camp.data.kit.logos as { assetId: string; variant: string }[];
  const copyGroups: [string, any[]][] = copy ? [['כותרות', copy.headlines ?? []], ['טקסטים', copy.texts ?? []], ['CTA', copy.ctas ?? []], ['גילוי נאות', copy.disclaimer ? [{ key: 'disclaimer', ...copy.disclaimer }] : []]] : [];

  return (
    <div className="stack">
      <div className="card">
        <h2 style={{ marginTop: 0 }}>מידות</h2>
        <p className="small muted">כשהמפרט מגדיר מידות — מוצגות רק המידות המותרות. כל מידה היא קומפוזיציה נפרדת.</p>
        {rows.map((r) => {
          const allowed = r.spec?.resolved.sizes;
          const chosen = sizes[r.placement.id] ?? [];
          return (
            <fieldset key={r.placement.id} className="pgroup">
              <legend>{r.site.name} · {r.placement.name}</legend>
              {Array.isArray(allowed) ? (
                <div className="row">{allowed.map((s) => { const k = `${s.width}x${s.height}`; const ex = exists(r.placement.id, k); return (
                  <label key={k} className="chk"><input type="checkbox" checked={ex || chosen.includes(k)} disabled={ex} onChange={() => toggle(r.placement.id, k)} /> <span className="mono">{s.width}×{s.height}</span>{ex && <span className="badge ok">קיים בסט</span>}</label>
                ); })}</div>
              ) : (
                <>
                  <div className="alert warn small">המידות במפרט לא ידועות. אפשר להזין מידה ידנית — יש לוודא אותה מול האתר.</div>
                  <div className="row" style={{ marginTop: 6 }}>
                    {chosen.map((k) => <label key={k} className="chk"><input type="checkbox" checked onChange={() => toggle(r.placement.id, k)} /> <span className="mono">{k.replace('x', '×')}</span></label>)}
                    <input className="mono" aria-label={`מידה ידנית ל${r.placement.name}`} placeholder="300x250" value={manual[r.placement.id] ?? ''} onChange={(e) => setManual((x) => ({ ...x, [r.placement.id]: e.target.value }))} style={{ width: 110 }} />
                    <button className="btn small" onClick={() => addManual(r.placement.id)}>הוסף מידה</button>
                  </div>
                </>
              )}
            </fieldset>
          );
        })}
        <ErrorBox error={mErr} />
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>חומרים</h2>
        <div className="small muted">לוגו מהמותג (לא נוצר ב-AI): {logos.length ? logos.map((l) => l.variant).join(', ') : 'אין לוגו בערכת המותג'}</div>
        <h3>תמונה ראשית</h3>
        {assets.loading && <Loading />}
        <div className="asset-pick" role="radiogroup" aria-label="תמונה ראשית">
          <label className={img === null ? 'on' : ''}><input type="radio" name="hero" checked={img === null} onChange={() => setImg(null)} /> ללא תמונה</label>
          {images.map((a) => (
            <label key={a.id} className={img === a.id ? 'on' : ''}>
              <input type="radio" name="hero" checked={img === a.id} onChange={() => setImg(a.id)} />
              <img src={a.url} alt="" />
              <span>{a.name}</span>
            </label>
          ))}
        </div>
        {images.length === 0 && !assets.loading && <div className="small muted">אין תמונות משויכות לקמפיין. <Link to={`/campaigns/${campaignId}`}>שיוך חומרים</Link></div>}
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>טקסטים מהקמפיין</h2>
        <p className="small muted">🔒 = טקסט קבוע בקמפיין: המודל אינו רשאי לשנות אותו.</p>
        {copyGroups.map(([g, arr]) => arr.length > 0 && (
          <fieldset key={g} className="pgroup">
            <legend>{g}</legend>
            {arr.map((t: any) => (
              <label key={t.key} className="chk" style={{ display: 'flex', margin: '3px 0' }}>
                <input type="checkbox" checked={(texts ?? []).includes(t.key)} onChange={() => setTexts((x) => (x ?? []).includes(t.key) ? (x ?? []).filter((k) => k !== t.key) : [...(x ?? []), t.key])} />
                <span className="mono small muted">{t.key}</span> <span>{t.text}</span> {t.locked && <span title="טקסט קבוע" aria-label="טקסט קבוע">🔒</span>}
              </label>
            ))}
          </fieldset>
        ))}
      </div>
      <div className="wz-nav">
        <button className="btn" onClick={onBack}>→ חזרה</button>
        <button className="btn primary" disabled={total === 0 && !variants.length} onClick={() => onNext({ sizes, imageAssetId: img, textKeys: texts ?? [] })}>המשך ({total} מידות) ←</button>
      </div>
    </div>
  );
}

// ── שלב 5: יצירה ──
function StepCreate({ setId, ws, prompt, data, reload, persist, onBack, onNext }: { setId: string; ws: WS; prompt: string; data: SetResp; reload: () => Promise<SetResp | null>; persist: (p: Partial<WS>) => Promise<WS>; onBack: () => void; onNext: () => void }) {
  const ai = useAiStatus();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [job, setJob] = useState<Job | null>(null);
  const [genSel, setGenSel] = useState<string[] | null>(null);
  const variants = data.variants;
  const nameOf = (vid: string) => { const v = variants.find((x) => x.id === vid); return v ? `${v.media_name} · ${v.width}×${v.height}` : vid; };

  const pending = useMemo(() => {
    const out: { placementId: string; width: number; height: number }[] = [];
    for (const pid of ws.placementIds ?? []) for (const s of ws.sizes?.[pid] ?? []) {
      const [w, h] = s.split('x').map(Number);
      if (!variants.some((v) => v.placement_id === pid && v.width === w && v.height === h)) out.push({ placementId: pid, width: w, height: h });
    }
    return out;
  }, [ws, variants]);

  // חידוש: עבודת יצירה קודמת
  useEffect(() => {
    if (!ws.lastGenerateJobId) return;
    let alive = true;
    waitForJob(ws.lastGenerateJobId, (j) => { if (alive) setJob(j); }).then(() => { if (alive) void reload(); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.lastGenerateJobId]);

  const create = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post<SetResp & { created: string[]; warnings: string[] }>(`/api/banner-sets/${setId}/variants`, { items: pending, imageAssetId: ws.imageAssetId ?? null });
      setWarnings(r.warnings ?? []);
      setGenSel(r.created);
      await reload();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true); setErr(null); setJob(null);
    try {
      const ids = genSel ?? variants.map((v) => v.id);
      const { jobId } = await api.post<{ jobId: string }>('/api/banner-sets/generate', { setId, prompt, variantIds: ids, idempotencyKey: crypto.randomUUID() });
      await persist({ lastGenerateJobId: jobId });
    } catch (e: any) { setErr(`הפעלת היצירה נכשלה: ${e.message}. הטיוטות לא נמחקו.`); } finally { setBusy(false); }
  };

  const sel = genSel ?? variants.map((v) => v.id);
  const running = job && (job.status === 'queued' || job.status === 'running');
  return (
    <div className="stack">
      <div className="card">
        <h2 style={{ marginTop: 0 }}>יצירת טיוטות</h2>
        {pending.length > 0 ? (
          <>
            <p>ייווצרו {pending.length} וריאציות מתבנית קבועה לכל משפחת יחס ממדים (בלי AI):</p>
            <ul className="small">{pending.map((p) => <li key={`${p.placementId}-${p.width}x${p.height}`}><span className="mono">{p.width}×{p.height}</span></li>)}</ul>
            <button className="btn primary" onClick={create} disabled={busy}>צור {pending.length} טיוטות</button>
          </>
        ) : <p className="small muted">כל המידות שנבחרו כבר קיימות בסט ({variants.length} וריאציות).</p>}
        {warnings.map((w) => <div key={w} className="alert warn small" style={{ marginTop: 6 }}>{w}</div>)}
        <ErrorBox error={err} />
      </div>

      {variants.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>הצעת קומפוזיציה ב-AI</h2>
          <AiStatusNote {...ai} />
          <p className="small muted">קומפוזיציה נפרדת לכל יחס ממדים. כשל ביצירה לא מוחק את הטיוטות הקיימות.</p>
          {!prompt.trim() && <div className="alert warn small">לא נכתבה הנחיה. <button className="linkish" onClick={onBack}>חזרה לכתיבת הנחיה</button></div>}
          <div className="row" style={{ margin: '6px 0' }}>
            {variants.map((v) => (
              <label key={v.id} className="chk"><input type="checkbox" checked={sel.includes(v.id)} onChange={() => setGenSel(sel.includes(v.id) ? sel.filter((x) => x !== v.id) : [...sel, v.id])} /> <span className="mono">{v.width}×{v.height}</span> <span className="small muted">{v.media_name}</span></label>
            ))}
          </div>
          <div className="row">
            <button className="btn primary" onClick={generate} disabled={busy || !!running || !sel.length}>הרץ יצירת AI ל-{sel.length} גדלים</button>
            <button className="btn" onClick={onNext}>המשך בלי AI / לרשימת הווריאציות ←</button>
          </div>
          {job && <div style={{ marginTop: 10 }}><JobProgress job={job} nameOf={nameOf} /></div>}
          {job?.status === 'failed' && <div className="alert warn small" style={{ marginTop: 6 }}>היצירה נכשלה. הטיוטות נשמרו ולא נמחקו — אפשר לערוך ידנית או לנסות שוב.</div>}
          {job?.status === 'succeeded' && <button className="btn primary" style={{ marginTop: 8 }} onClick={onNext}>לרשימת הווריאציות ←</button>}
        </div>
      )}
      <div className="wz-nav"><button className="btn" onClick={onBack}>→ חזרה</button><span /></div>
    </div>
  );
}

// ── שלב 6: וריאציות ──
function StepResults({ setId, data, onBack }: { setId: string; data: SetResp; onBack: () => void }) {
  if (!data.variants.length) return <div className="card"><Empty>אין עדיין וריאציות בסט. <button className="linkish" onClick={onBack}>ליצירה</button></Empty></div>;
  return (
    <div className="stack">
      <div className="row between">
        <h2 style={{ margin: 0 }}>וריאציות ({data.variants.length})</h2>
        <Link className="btn primary" to={`/exports?setId=${setId}`}>לבדיקות ויצוא ←</Link>
      </div>
      <div className="wz-thumbs">
        {data.variants.map((v) => <ResultCard key={v.id} v={v} />)}
      </div>
      <div className="wz-nav"><button className="btn" onClick={onBack}>→ חזרה</button><span /></div>
    </div>
  );
}

function ResultCard({ v }: { v: VariantRow }) {
  const { ctx, err } = useVariantCtx(v.id, v.current_revision);
  const issues = ctx?.issues ?? [];
  return (
    <article className="wz-thumb" aria-label={`${v.display_name} ${v.width}x${v.height}`}>
      <VariantThumb ctx={ctx} />
      <div className="row between">
        <strong className="mono">{v.width}×{v.height}</strong>
        <span className="small muted">גרסה {v.current_revision}</span>
      </div>
      <div className="small">{v.media_name} · {v.placement_name}</div>
      <div className="row small">
        <span className={`badge ${v.spec_status === 'approved' ? 'ok' : 'warn'}`}>מפרט: {SPEC_STATUS_LABEL[v.spec_status] ?? v.spec_status}{v.spec_status !== 'approved' ? ' · טיוטה בלבד' : ''}</span>
        {v.spec_is_demo && <span className="badge">הדגמה</span>}
        {v.last_export && <span className="badge info">יצוא אחרון: {v.last_export.kind} · {v.last_export.status}</span>}
      </div>
      <ErrorBox error={err} />
      {issues.length > 0 ? (
        <ul className="iss-list">{issues.map((i, k) => <li key={k}><span className={`badge ${i.severity === 'error' ? 'err' : 'warn'}`}>{i.severity === 'error' ? 'שגיאה' : 'אזהרה'}</span> {i.message}</li>)}</ul>
      ) : ctx && <div className="small" style={{ color: 'var(--ok)' }}>אין בעיות גאומטריה</div>}
      <div className="row">
        <Link className="btn small primary" to={`/variants/${v.id}/edit`}>לעורך</Link>
        <Link className="btn small" to={`/exports?variantId=${v.id}`}>בדיקות ויצוא</Link>
      </div>
    </article>
  );
}
